/**
 * OIDC Authentication Module
 *
 * Provides Keycloak OIDC integration for React applications.
 *
 * @module @project-x/auth/oidc
 */

// Types
export type {
  OIDCConfig,
  PKCEParams,
  AuthState,
  LoginOptions,
  TokenResponse,
  TokenClaims,
  OIDCUser,
  EnsureUserResponse,
  OIDCError,
  AuthSession,
  RoleCheck,
  KeycloakRole,
} from "./types";

export { KEYCLOAK_ROLES } from "./types";

// PKCE utilities
export { generateState, generateCodeVerifier, generateCodeChallenge, generatePKCE, AUTH_STATE_KEY } from "./pkce";

// Configuration
export {
  ENV_VARS,
  getKeycloakUrl,
  getServerKeycloakUrl,
  getKeycloakRealm,
  getKeycloakAdminRealm,
  getClientId,
  getPublicScheme,
  getPublicHost,
  getUsersUrl,
  getServerUsersUrl,
  getRealmUrl,
  getAuthorizationEndpoint,
  getTokenEndpoint,
  getLogoutEndpoint,
  getJwksEndpoint,
  getUserinfoEndpoint,
  createOIDCConfig,
  CLIENT_IDS,
  type ClientId,
} from "./config";

// Client functions
export {
  OIDCClientError,
  decodeToken,
  isTokenExpired,
  isAuthFresh,
  storeAuthState,
  retrieveAuthState,
  cleanupStaleAuthState,
  buildAuthorizationUrl,
  initiateLogin,
  forceReauth,
  exchangeCode,
  refreshTokens,
  ensureUser,
  extractRolesFromClaims,
  createUser,
  buildLogoutUrl,
  initiateLogout,
  handleCallback,
  roleHelpers,
} from "./client";

// Direct Access Grant error classification
export {
  classifyDirectGrantError,
  type DirectGrantErrorKind,
  type DirectGrantErrorResult,
} from "./direct-grant-error";

// React hooks and components
export {
  AuthProvider,
  useAuth,
  useUser,
  useIsAuthenticated,
  useAccessToken,
  useRoles,
  AuthGate,
  useOIDCCallback,
  type AuthProviderProps,
  type AuthContextValue,
  type AuthGateProps,
} from "./hooks";

